// ============================================================================
// Chronological normalization for dual-clock ingestion.
//
// Two clocks feed health_metrics:
//   * Live BLE/ANT+ telemetry  — captured in raw client-local wall time.
//   * Vital/Junction webhooks  — arrive async (minutes-hours late) as UTC ISO.
//
// Both must collapse onto the SAME "physiological day" the user's circadian
// configuration defines, otherwise a late Oura sync lands on the UTC calendar
// day while a live row sits on the local day — splitting one session across two
// protocol windows. This module is the single source of truth for that mapping.
//
// Circadian config lives on profiles as `circadian_phase` + `bio_window_hours`
// (migration 20250618). There is no `biological_day_offset` column; callers
// referencing that name should use this util instead.
// ============================================================================

export type CircadianPhase = 'standard' | 'night_shift' | 'split_sleep'

export type CircadianContext = {
  timezone: string
  circadian_phase: CircadianPhase
  bio_window_hours: number
  /** Optional explicit anchor wake instant from resolvePhysiologicalDay. */
  anchor_wake_at?: string | null
}

/** Local calendar date (YYYY-MM-DD) for an instant in a timezone. */
export function localDateFromInstant(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone || 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso))
}

export function localHourFromInstant(iso: string, timezone: string): number {
  return Number(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone || 'UTC',
      hour: 'numeric',
      hour12: false,
    }).format(new Date(iso)),
  )
}

/**
 * Resolve the physiological day an instant belongs to.
 *
 * - standard:     local calendar date, but pre-dawn hours (< 03:00 local) roll
 *                 back to the prior day so a 01:00 finish counts as "yesterday".
 * - night_shift:  the active block spans the calendar boundary; instants before
 *                 the local noon pivot belong to the prior physiological day.
 * - split_sleep:  anchored to the explicit wake instant when available.
 */
export function physiologicalDayForInstant(iso: string, ctx: CircadianContext): string {
  const tz = ctx.timezone || 'UTC'
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return localDateFromInstant(new Date().toISOString(), tz)

  if (ctx.circadian_phase === 'split_sleep' && ctx.anchor_wake_at) {
    return localDateFromInstant(ctx.anchor_wake_at, tz)
  }

  const hour = localHourFromInstant(iso, tz)
  const PRE_DAWN_ROLLBACK_HOUR = 3
  const NIGHT_SHIFT_PIVOT_HOUR = 12

  const pivot = ctx.circadian_phase === 'night_shift' ? NIGHT_SHIFT_PIVOT_HOUR : PRE_DAWN_ROLLBACK_HOUR
  if (hour < pivot) {
    return localDateFromInstant(new Date(ms - 24 * 3_600_000).toISOString(), tz)
  }
  return localDateFromInstant(iso, tz)
}

/**
 * Normalize whatever timestamp shape a Vital payload carries into a strict
 * physiological metric_date. Falls back to now() only when no instant exists.
 */
export function metricDateFromPayload(
  data: Record<string, unknown>,
  ctx: CircadianContext,
): string {
  const candidate =
    (data.bedtime_stop as string) ??
    (data.time_end as string) ??
    (data.time_start as string) ??
    (data.timestamp as string) ??
    (data.date as string) ??
    (data.calendar_date as string)

  if (!candidate) return localDateFromInstant(new Date().toISOString(), ctx.timezone)
  // Bare YYYY-MM-DD provider dates are already day-resolved — trust them.
  if (/^\d{4}-\d{2}-\d{2}$/.test(candidate)) return candidate
  return physiologicalDayForInstant(candidate, ctx)
}

/** Default context for users with no circadian config loaded yet. */
export function defaultCircadianContext(timezone = 'UTC'): CircadianContext {
  return { timezone, circadian_phase: 'standard', bio_window_hours: 20, anchor_wake_at: null }
}
